import React, { useState, useEffect } from 'react'

import {
  Dialog, DialogTitle as MuiDialogTitle, Typography,
  IconButton, DialogContent as MuiDialogContent,
  InputLabel, FormControl, FormHelperText, FilledInput,
  InputAdornment, Button as MuiButton
} from '@material-ui/core'

import PropTypes from 'prop-types'

import { makeStyles } from '@material-ui/core/styles'

import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import CloseIcon from '@material-ui/icons/Close'

import clsx from 'clsx'

const useStyles = makeStyles(theme => ({
  resetBtn: {
    backgroundColor: '#5291fc',
    color: '#fff',
    width: '100%',
    '&:hover': {
      backgroundColor: '#1F5EC9',
      color: '#fff'
    },
    margin: 'auto'
  },
  passwordfield: {
    backgroundColor: '#EFF5F7',
    opacity: 0.75,
    '&:hover': {
      backgroundColor: '#BCC2C4'
    },
    width: '100%',
    margin: 'auto'
  },
  margin: {
    margin: theme.spacing(1)
  },
  textField: {
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  deleteBtn: {
    borderColor: '#d9534f',
    color: '#d9534f',
    width: '75%',
    '&:hover': {
      backgroundColor: '#d9534f',
      color: '#fff'
    }
  },
  greenText: {
    color: '#97b557'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
}))

/**
 * Component displays a modal used to reset password or prompt user for password
 * @param {Object} props props object
 ** @param {Boolean} passwordModal state for modal
 ** @param {Function} togglePasswordModal function to close modal
 ** @param {String} passwordCase string identifier to difference between cases
 ** @param {String} firstName subset of passwordCase "admin-case-reset" | users first name
 ** @param {String} lastName subset of passwordCase "admin-case-reset" | users last name
 ** @param {Function} onResetPassword function used when a user clicks confirm
 */
const ResetPassword = (props) => {
  const { passwordModal, togglePasswordModal, passwordCase } = props

  const { firstName, lastName, onResetPassword, isSSO = false } = props

  const MuiClasses = useStyles()

  const [adminPassword, setAdminPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmError, setConfirmError] = useState(false)

  const [showPassword, setShowPassword] = useState({
    adminPassword: false,
    newPassword: false,
    confirmPassword: false
  })

  useEffect(() => {
    if (confirmPassword !== '') {
      confirmPassword === newPassword ? setConfirmError(false) : setConfirmError(true)
    }
  }, [confirmPassword, newPassword])

  const handleToggleModal = () => {
    setAdminPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setConfirmError(false)
    togglePasswordModal()
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    const resetStat = await onResetPassword(adminPassword, newPassword, confirmPassword)
    if (resetStat === true) {
      setAdminPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setConfirmError(false)
    }
  }

  const handleDelete = async (e) => {
    e.preventDefault()
    await onResetPassword(adminPassword)
  }

  return (
    <>
      <Dialog open={passwordModal} onClose={handleToggleModal} className='filterModal' fullWidth maxWidth='sm'>
        <MuiDialogTitle disableTypography>
          <Typography
            variant='h4'
            color='primary'
            classes={{ colorPrimary: MuiClasses.greenText }}
            className='font-weight-bold'
          >
            {passwordCase === 'admin-case-delete' ? `Delete ${firstName}?` : passwordCase === 'admin-case-reset' ? `Reset ${firstName} ${lastName}'s Password` : 'Reset Your Password'}
          </Typography>

          <IconButton aria-label='close' className={MuiClasses.closeButton} onClick={handleToggleModal}>
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>

        <MuiDialogContent dividers>
          {
            passwordCase === 'admin-case-delete'
              ? (
                <>
                  {
                    !isSSO && (
                      <FormControl className={clsx(MuiClasses.margin, MuiClasses.textField)} variant='filled'>
                        <InputLabel htmlFor='admin-password'>
                          Enter your password to delete this user
                        </InputLabel>

                        <FilledInput
                          id='admin-password'
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          className={`w-100 mb-2 ${MuiClasses.passwordfield}`}
                          variant='filled'
                          type={showPassword.adminPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position='end'>
                              <IconButton
                                aria-label='toggle password visibility'
                                onClick={(e) => {
                                  e.preventDefault()
                                  setShowPassword({ ...showPassword, adminPassword: !showPassword.adminPassword })
                                }}
                              >
                                {showPassword.adminPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                        <FormHelperText>Click confirm after entering your password to delete this user</FormHelperText>
                      </FormControl>
                    )
                  }

                  <MuiButton variant='outlined' className={`mx-auto ${MuiClasses.deleteBtn}`} disabled={!adminPassword && !isSSO} onClick={handleDelete}>Confirm</MuiButton>
                </>)
              : (
                <>
                  {
                    !isSSO && (
                      <FormControl className={clsx(MuiClasses.margin, MuiClasses.textField)} variant='filled'>
                        <InputLabel htmlFor='admin-password'>
                          {passwordCase === 'admin-case-reset' ? 'Your Password' : 'Your Current Password'}
                        </InputLabel>
                        <FilledInput
                          id='admin-password'
                          value={adminPassword}
                          onChange={(e) => setAdminPassword(e.target.value)}
                          className={`w-100 mb-2 ${MuiClasses.passwordfield}`}
                          variant='filled'
                          type={showPassword.adminPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position='end'>
                              <IconButton
                                aria-label='toggle password visibility'
                                onClick={(e) => {
                                  e.preventDefault()
                                  setShowPassword({ ...showPassword, adminPassword: !showPassword.adminPassword })
                                }}
                              >
                                {showPassword.adminPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          }
                        />
                      </FormControl>
                    )
                  }

                  <FormControl className={clsx(MuiClasses.margin, MuiClasses.textField)} variant='filled'>
                    <InputLabel htmlFor='new-supervisor-password'>
                      {passwordCase === 'admin-case-reset' ? 'New Supervisor Password' : 'New Password'}
                    </InputLabel>
                    <FilledInput
                      id='new-supervisor-password'
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`w-100 mb-2 ${MuiClasses.passwordfield}`}
                      variant='filled'
                      type={showPassword.newPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={(e) => {
                              e.preventDefault()
                              setShowPassword({ ...showPassword, newPassword: !showPassword.newPassword })
                            }}
                          >
                            {showPassword.newPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>

                  <FormControl className={clsx(MuiClasses.margin, MuiClasses.textField)} variant='filled'>
                    <InputLabel htmlFor='confirm-supervisor-password'>
                      {passwordCase === 'admin-case-reset' ? 'Confirm New Supervisor Password' : 'Confirm New Password'}
                    </InputLabel>
                    <FilledInput
                      id='confirm-supervisor-password'
                      value={confirmPassword}
                      error={confirmError}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value)
                      }}
                      className={`w-100 mb-2 ${MuiClasses.passwordfield} border border-bottom-success`}
                      variant='filled'
                      type={showPassword.confirmPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={(e) => {
                              e.preventDefault()
                              setShowPassword({ ...showPassword, confirmPassword: !showPassword.confirmPassword })
                            }}
                          >
                            {showPassword.confirmPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>

                  <MuiButton
                    variant='contained'
                    size='large'
                    disabled={
                      (isSSO && (!confirmPassword || !newPassword)) || (!isSSO && (!confirmPassword || !newPassword || !adminPassword))
                    }
                    className={`mx-auto ${MuiClasses.resetBtn}`}
                    onClick={handleResetPassword}
                  >
                    Confirm
                  </MuiButton>
                </>)
          }
        </MuiDialogContent>
      </Dialog>
    </>
  )
}

ResetPassword.propTypes = {
  passwordModal: PropTypes.bool.isRequired,
  // Show Modal state
  togglePasswordModal: PropTypes.func.isRequired,
  // Function to hide modal
  passwordCase: PropTypes.string.isRequired,
  // Case to difference between a user resetting their password and an admin resetting someone's password
  firstName: PropTypes.string,
  // first name of user in case of admin case password reset
  lastName: PropTypes.string,
  // last name of user in case of admin case password reset
  onResetPassword: PropTypes.func.isRequired
  // Function triggered when Confirm button is clicked
}

export default ResetPassword
